import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddIcon from '@mui/icons-material/Add'
import UndoIcon from '@mui/icons-material/Undo'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import BuildIcon from '@mui/icons-material/Build'
import type { VariableMetadata, CodeFrameItem } from '../../hooks/useVariableEditor'

interface VariableEditorModalProps {
  open: boolean
  isLoading: boolean
  isSaving: boolean
  isConstructing: boolean
  error: string | null
  saveResult: string[] | null
  metadata: VariableMetadata | null
  // T19: a constructed variable (metadata.constructorDef is set) whose name is
  // NOT in the job's construct registry (job_constructs) is a data-file
  // construct — its rules come from the source file, not from a row this
  // editor can PUT. Registered constructs are edited through
  // ConstructEditorDialog instead (Variables.tsx routes "Edit" there), so
  // this flag only ever makes the Constructor tab / Syntax column read-only;
  // it never hides them, and Reconstruct always stays enabled either way.
  isRegisteredConstruct: boolean
  onClose: () => void
  onSave: (updates: {
    description?: string
    codes?: Record<string, string>
    addCodes?: Record<string, string>
    removeCodes?: string[]
    nets?: Record<string, string>
    addNets?: Record<string, string>
    removeNets?: string[]
    constructorSyntax?: Record<string, string>
    noSort?: string[]
  }) => Promise<any>
  onConstruct: () => Promise<any>
}

interface ItemEdit {
  type: 'code' | 'net' | 'arith'
  key: string
  label: string
  originalLabel: string
  syntax: string
  originalSyntax: string
  isNew: boolean
  isRemoved: boolean
  noSort: boolean
  originalNoSort: boolean
}

const TYPE_COLORS: Record<string, string> = {
  code: 'default',
  net: 'info',
  arith: 'warning',
}

const TYPE_LABELS: Record<string, string> = {
  code: 'Code',
  net: 'Net',
  arith: 'Stat',
}

const VariableEditorModal: React.FC<VariableEditorModalProps> = ({
  open,
  isLoading,
  isSaving,
  isConstructing,
  error,
  saveResult,
  metadata,
  isRegisteredConstruct,
  onClose,
  onSave,
  onConstruct,
}) => {
  const [tab, setTab] = useState(0)
  const [description, setDescription] = useState('')
  const [originalDescription, setOriginalDescription] = useState('')
  const [itemEdits, setItemEdits] = useState<ItemEdit[]>([])
  const [newCodeKey, setNewCodeKey] = useState('')
  const [newCodeLabel, setNewCodeLabel] = useState('')
  const [newItemType, setNewItemType] = useState<'code' | 'net'>('code')
  const [netBuilderOpen, setNetBuilderOpen] = useState(false)
  const [netBuilderLabel, setNetBuilderLabel] = useState('')
  const [netBuilderCodes, setNetBuilderCodes] = useState<Set<string>>(new Set())

  // Reset state when metadata changes
  useEffect(() => {
    if (!metadata) return
    setDescription(metadata.description)
    setOriginalDescription(metadata.description)
    setTab(0)
    setNewCodeKey('')
    setNewCodeLabel('')

    // Build edits from all frames
    const frame = metadata.frames[0]
    if (!frame) {
      setItemEdits([])
      return
    }
    const noSortSet = new Set(frame.noSort || [])

    setItemEdits(
      (frame.items || []).map(item => ({
        type: item.type,
        key: item.key,
        label: item.label,
        originalLabel: item.label,
        syntax: item.syntax || '',
        originalSyntax: item.syntax || '',
        isNew: false,
        isRemoved: false,
        noSort: noSortSet.has(item.key) || noSortSet.has(`_${item.key}`),
        originalNoSort: noSortSet.has(item.key) || noSortSet.has(`_${item.key}`),
      }))
    )
  }, [metadata])

  const handleLabelChange = (index: number, newLabel: string) => {
    setItemEdits(prev => prev.map((c, i) =>
      i === index ? { ...c, label: newLabel } : c
    ))
  }

  const handleSyntaxChange = (index: number, newSyntax: string) => {
    setItemEdits(prev => prev.map((c, i) =>
      i === index ? { ...c, syntax: newSyntax } : c
    ))
  }

  const handleToggleRemove = (index: number) => {
    const item = itemEdits[index]
    if (item.isNew) {
      setItemEdits(prev => prev.filter((_, i) => i !== index))
    } else {
      setItemEdits(prev => prev.map((c, i) =>
        i === index ? { ...c, isRemoved: !c.isRemoved } : c
      ))
    }
  }

  const handleRestoreItem = (index: number) => {
    setItemEdits(prev => prev.map((c, i) =>
      i === index ? { ...c, isRemoved: false, label: c.originalLabel } : c
    ))
  }

  const handleToggleNoSort = (index: number) => {
    setItemEdits(prev => prev.map((c, i) =>
      i === index ? { ...c, noSort: !c.noSort } : c
    ))
  }

  const handleMoveUp = (index: number) => {
    if (index <= 0) return
    setItemEdits(prev => {
      const next = [...prev]
      ;[next[index - 1], next[index]] = [next[index], next[index - 1]]
      return next
    })
  }

  const handleMoveDown = (index: number) => {
    if (index >= itemEdits.length - 1) return
    setItemEdits(prev => {
      const next = [...prev]
      ;[next[index], next[index + 1]] = [next[index + 1], next[index]]
      return next
    })
  }

  const handleAddItem = () => {
    if (!newCodeKey.trim()) return
    if (itemEdits.some(c => c.key === newCodeKey.trim() && !c.isRemoved)) return

    setItemEdits(prev => [...prev, {
      type: newItemType,
      key: newCodeKey.trim(),
      label: newCodeLabel.trim(),
      originalLabel: '',
      syntax: '',
      originalSyntax: '',
      isNew: true,
      isRemoved: false,
      noSort: false,
      originalNoSort: false,
    }])
    setNewCodeKey('')
    setNewCodeLabel('')
  }

  const handleSave = async () => {
    const updates: {
      description?: string
      codes?: Record<string, string>
      addCodes?: Record<string, string>
      removeCodes?: string[]
      nets?: Record<string, string>
      addNets?: Record<string, string>
      removeNets?: string[]
      constructorSyntax?: Record<string, string>
      noSort?: string[]
    } = {}

    if (description !== originalDescription) {
      updates.description = description
    }

    // Changed code labels
    const changedCodes: Record<string, string> = {}
    for (const edit of itemEdits) {
      if (!edit.isNew && !edit.isRemoved && edit.type === 'code' && edit.label !== edit.originalLabel) {
        changedCodes[edit.key] = edit.label
      }
    }
    if (Object.keys(changedCodes).length > 0) updates.codes = changedCodes

    // New codes
    const newCodes: Record<string, string> = {}
    for (const edit of itemEdits) {
      if (edit.isNew && !edit.isRemoved && edit.type === 'code') {
        newCodes[edit.key] = edit.label
      }
    }
    if (Object.keys(newCodes).length > 0) updates.addCodes = newCodes

    // Removed codes
    const removedCodes = itemEdits.filter(c => c.isRemoved && !c.isNew && c.type === 'code').map(c => c.key)
    if (removedCodes.length > 0) updates.removeCodes = removedCodes

    // Changed net labels
    const changedNets: Record<string, string> = {}
    for (const edit of itemEdits) {
      if (!edit.isNew && !edit.isRemoved && edit.type === 'net' && edit.label !== edit.originalLabel) {
        changedNets[edit.key] = edit.label
      }
    }
    if (Object.keys(changedNets).length > 0) updates.nets = changedNets

    // New nets
    const newNets: Record<string, string> = {}
    for (const edit of itemEdits) {
      if (edit.isNew && !edit.isRemoved && edit.type === 'net') {
        newNets[edit.key] = edit.label
      }
    }
    if (Object.keys(newNets).length > 0) updates.addNets = newNets

    // Removed nets
    const removedNets = itemEdits.filter(c => c.isRemoved && !c.isNew && c.type === 'net').map(c => c.key)
    if (removedNets.length > 0) updates.removeNets = removedNets

    // Changed constructor syntax
    const changedSyntax: Record<string, string> = {}
    for (const edit of itemEdits) {
      if (!edit.isNew && !edit.isRemoved && edit.type === 'code' && edit.syntax !== edit.originalSyntax) {
        changedSyntax[edit.key] = edit.syntax
      }
    }
    if (Object.keys(changedSyntax).length > 0) updates.constructorSyntax = changedSyntax

    // NoSort changes
    if (noSortChanges > 0) {
      updates.noSort = itemEdits.filter(e => e.noSort && !e.isRemoved).map(e => e.key)
    }

    if (Object.keys(updates).length === 0) return

    try {
      await onSave(updates)
    } catch {
      // error handled by hook
    }
  }

  // Count pending changes
  const descriptionChanged = description !== originalDescription
  const labelChanges = itemEdits.filter(c => !c.isNew && !c.isRemoved && c.label !== c.originalLabel).length
  const syntaxChanges = itemEdits.filter(c => !c.isNew && !c.isRemoved && c.syntax !== c.originalSyntax).length
  const newItems = itemEdits.filter(c => c.isNew && !c.isRemoved).length
  const removedItems = itemEdits.filter(c => c.isRemoved && !c.isNew).length
  const noSortChanges = itemEdits.filter(c => c.noSort !== c.originalNoSort).length
  const totalChanges = (descriptionChanged ? 1 : 0) + labelChanges + syntaxChanges + newItems + removedItems + noSortChanges
  const hasChanges = totalChanges > 0
  const hasConstructor = !!metadata?.constructorDef
  // A constructed variable that isn't in the registry got its rules from the
  // job's data files (a raw [Constructor] section with no job_constructs
  // row) — those rules can only be changed by re-importing, so the Syntax
  // column and Constructor tab go read-only. Reconstruct (rebuilding .cd
  // from the existing rules) is unaffected.
  const isDataFileConstruct = hasConstructor && !isRegisteredConstruct

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { height: '80vh', display: 'flex', flexDirection: 'column' } }}
    >
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        Edit Variable
        {metadata && (
          <Typography variant="body2" component="span" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
            {metadata.name}
          </Typography>
        )}
        {metadata && metadata.cases > 0 && (
          <Typography variant="caption" component="span" sx={{ color: 'text.disabled' }}>
            ({metadata.cases.toLocaleString()} cases)
          </Typography>
        )}
        {hasChanges && (
          <Chip
            size="small"
            label={`${totalChanges} change${totalChanges !== 1 ? 's' : ''}`}
            color="info"
            variant="outlined"
            sx={{ ml: 'auto' }}
          />
        )}
      </DialogTitle>

      <DialogContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 0 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress size={32} />
          </Box>
        ) : !metadata ? (
          <Box sx={{ p: 3 }}>
            <Typography color="text.secondary">No variable selected</Typography>
          </Box>
        ) : (
          <>
            <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ px: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Tab label="Details" />
              <Tab label={`Codes (${itemEdits.filter(c => !c.isRemoved).length})`} />
              {hasConstructor && <Tab label="Constructor" />}
            </Tabs>

            {/* Details tab */}
            {tab === 0 && (
              <Box sx={{ p: 3, overflow: 'auto' }}>
                <TextField
                  label="Variable Name"
                  value={metadata.name}
                  disabled
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                  InputProps={{ sx: { fontFamily: 'monospace' } }}
                />
                <TextField
                  label="Description"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  maxRows={6}
                  sx={{ mb: 2 }}
                  helperText={descriptionChanged ? 'Modified' : undefined}
                  FormHelperTextProps={{ sx: { color: 'info.main' } }}
                />
                {metadata.dataType && (
                  <TextField
                    label="Data Type"
                    value={metadata.dataType}
                    disabled
                    fullWidth
                    size="small"
                    sx={{ mb: 2 }}
                  />
                )}
                {metadata.special && Object.keys(metadata.special).length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>
                      Special Properties
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableBody>
                          {Object.entries(metadata.special).map(([key, value]) => (
                            <TableRow key={key}>
                              <TableCell sx={{ fontFamily: 'monospace', width: '40%', color: 'text.secondary' }}>
                                {key}
                              </TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>
                                {value}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Box>
            )}

            {/* Codes tab */}
            {tab === 1 && (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {/* Add item row */}
                <Box sx={{ display: 'flex', gap: 1, px: 3, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', alignItems: 'center' }}>
                  <TextField
                    placeholder="Key"
                    value={newCodeKey}
                    onChange={e => setNewCodeKey(e.target.value)}
                    size="small"
                    sx={{ width: 120 }}
                    inputProps={{ style: { fontFamily: 'monospace' } }}
                  />
                  <TextField
                    placeholder="Label"
                    value={newCodeLabel}
                    onChange={e => setNewCodeLabel(e.target.value)}
                    size="small"
                    sx={{ flex: 1 }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleAddItem()
                    }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    color="info"
                    startIcon={<AddIcon />}
                    onClick={handleAddItem}
                    disabled={!newCodeKey.trim()}
                  >
                    Add
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="info"
                    onClick={() => {
                      setNetBuilderOpen(true)
                      setNetBuilderLabel('')
                      setNetBuilderCodes(new Set())
                    }}
                  >
                    + Net
                  </Button>
                </Box>

                {/* Items list */}
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 50 }}>Type</TableCell>
                        <TableCell sx={{ width: 80 }}>Key</TableCell>
                        <TableCell>Label</TableCell>
                        {hasConstructor && <TableCell sx={{ width: 180 }}>Syntax</TableCell>}
                        <TableCell sx={{ width: 60 }} align="center">NoSort</TableCell>
                        <TableCell sx={{ width: 100 }} align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {itemEdits.map((edit, index) => (
                        <TableRow
                          key={`${edit.type}-${edit.key}-${index}`}
                          sx={{
                            opacity: edit.isRemoved ? 0.4 : 1,
                            bgcolor: edit.isNew ? 'action.hover' : 'transparent',
                          }}
                        >
                          <TableCell>
                            <Chip
                              size="small"
                              label={TYPE_LABELS[edit.type] || edit.type}
                              color={TYPE_COLORS[edit.type] as any || 'default'}
                              variant="outlined"
                              sx={{ height: 20, fontSize: 11 }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                            {edit.key}
                            {edit.isNew && (
                              <Chip size="small" label="new" color="success" variant="outlined" sx={{ ml: 0.5, height: 18, fontSize: 10 }} />
                            )}
                          </TableCell>
                          <TableCell>
                            {edit.isRemoved ? (
                              <Typography variant="body2" sx={{ fontSize: 13, textDecoration: 'line-through' }}>{edit.label}</Typography>
                            ) : (
                              <TextField
                                value={edit.label}
                                onChange={e => handleLabelChange(index, e.target.value)}
                                size="small"
                                fullWidth
                                variant="standard"
                                InputProps={{
                                  disableUnderline: edit.label === edit.originalLabel,
                                  sx: { fontSize: 13 },
                                }}
                              />
                            )}
                          </TableCell>
                          {hasConstructor && (
                            <TableCell sx={{ maxWidth: 200 }}>
                              {edit.type === 'code' && !edit.isRemoved && isDataFileConstruct ? (
                                <Tooltip title="Rules come from the job's data files and can't be edited here">
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}>
                                    {edit.syntax || '-'}
                                  </Typography>
                                </Tooltip>
                              ) : edit.type === 'code' && !edit.isRemoved ? (
                                <TextField
                                  value={edit.syntax}
                                  onChange={e => handleSyntaxChange(index, e.target.value)}
                                  size="small"
                                  fullWidth
                                  variant="standard"
                                  placeholder="filter [increment]"
                                  InputProps={{
                                    disableUnderline: edit.syntax === edit.originalSyntax,
                                    sx: { fontFamily: 'monospace', fontSize: 11 },
                                  }}
                                />
                              ) : (
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}>
                                  {edit.syntax || '-'}
                                </Typography>
                              )}
                            </TableCell>
                          )}
                          <TableCell align="center">
                            <Checkbox
                              size="small"
                              checked={edit.noSort}
                              onChange={() => handleToggleNoSort(index)}
                              disabled={edit.isRemoved}
                              sx={{ p: 0 }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <IconButton size="small" onClick={() => handleMoveUp(index)} disabled={index === 0 || edit.isRemoved} sx={{ p: 0.25 }}>
                              <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleMoveDown(index)} disabled={index === itemEdits.length - 1 || edit.isRemoved} sx={{ p: 0.25 }}>
                              <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                            {edit.isRemoved ? (
                              <Tooltip title="Restore">
                                <IconButton size="small" onClick={() => handleRestoreItem(index)} sx={{ p: 0.25 }}>
                                  <UndoIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title={edit.isNew ? 'Remove' : 'Mark for removal'}>
                                <IconButton size="small" onClick={() => handleToggleRemove(index)} color="error" sx={{ p: 0.25 }}>
                                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {itemEdits.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={hasConstructor ? 6 : 5} align="center">
                            <Typography variant="body2" color="text.secondary">No codes</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Change summary */}
                {(labelChanges > 0 || syntaxChanges > 0 || newItems > 0 || removedItems > 0 || noSortChanges > 0) && (
                  <Box sx={{ px: 3, py: 1, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {labelChanges > 0 && <Chip size="small" label={`${labelChanges} label${labelChanges !== 1 ? 's' : ''} changed`} color="info" variant="outlined" />}
                    {syntaxChanges > 0 && <Chip size="small" label={`${syntaxChanges} syntax changed`} color="warning" variant="outlined" />}
                    {newItems > 0 && <Chip size="small" label={`${newItems} new`} color="success" variant="outlined" />}
                    {removedItems > 0 && <Chip size="small" label={`${removedItems} removed`} color="error" variant="outlined" />}
                    {noSortChanges > 0 && <Chip size="small" label={`${noSortChanges} sort changed`} color="warning" variant="outlined" />}
                  </Box>
                )}
              </Box>
            )}

            {/* Constructor tab */}
            {tab === 2 && metadata?.constructorDef && (
              <Box sx={{ p: 3, overflow: 'auto' }}>
                {isDataFileConstruct && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Rules come from the job's data files and can't be edited here.
                  </Alert>
                )}
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Flags: <code>{metadata.constructorDef.flags}</code>
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Code</TableCell>
                        <TableCell>Rule</TableCell>
                        <TableCell>Filter</TableCell>
                        <TableCell>Increment</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {metadata.constructorDef.lines.map((line, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                            {line.codeTag}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                            {line.expression}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                            {line.filter}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                            {line.increment || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mx: 3, mt: 1 }}>{error}</Alert>
        )}
        {saveResult && (
          <Alert severity="success" sx={{ mx: 3, mt: 1 }}>
            Saved: {saveResult.join(', ')}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Box>
          {hasConstructor && (
            <Button
              variant="outlined"
              color="warning"
              onClick={onConstruct}
              disabled={isConstructing || isSaving || isLoading}
              startIcon={isConstructing ? <CircularProgress size={16} /> : <BuildIcon />}
              size="small"
            >
              {isConstructing ? 'Constructing...' : 'Reconstruct Data'}
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>
            {saveResult ? 'Close' : 'Cancel'}
          </Button>
          <Button
            variant="contained"
            color="info"
            onClick={handleSave}
            disabled={!hasChanges || isSaving || isLoading}
            startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </DialogActions>

      {/* Net Builder Dialog */}
      <Dialog open={netBuilderOpen} onClose={() => setNetBuilderOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Net</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select codes to combine into a net, then give it a label.
          </Typography>
          <TextField
            label="Net Label"
            value={netBuilderLabel}
            onChange={e => setNetBuilderLabel(e.target.value)}
            fullWidth
            size="small"
            autoFocus
            sx={{ mb: 2 }}
          />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Select codes:</Typography>
          <Box sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
            {itemEdits.filter(e => e.type === 'code' && !e.isRemoved).map(edit => (
              <FormControlLabel
                key={edit.key}
                control={
                  <Checkbox
                    size="small"
                    checked={netBuilderCodes.has(edit.key)}
                    onChange={() => {
                      setNetBuilderCodes(prev => {
                        const next = new Set(prev)
                        if (next.has(edit.key)) next.delete(edit.key)
                        else next.add(edit.key)
                        return next
                      })
                    }}
                  />
                }
                label={
                  <Typography variant="body2">
                    <span style={{ fontFamily: 'monospace', marginRight: 8 }}>{edit.key}</span>
                    {edit.label}
                  </Typography>
                }
                sx={{ display: 'block', ml: 0 }}
              />
            ))}
          </Box>
          {netBuilderCodes.size > 0 && (
            <Typography variant="caption" sx={{ mt: 1, display: 'block', fontFamily: 'monospace', color: 'text.secondary' }}>
              net({Array.from(netBuilderCodes).sort((a, b) => Number(a) - Number(b)).join(';')})
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNetBuilderOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="info"
            disabled={netBuilderCodes.size < 2 || !netBuilderLabel.trim()}
            onClick={() => {
              const sortedCodes = Array.from(netBuilderCodes).sort((a, b) => Number(a) - Number(b))
              const netKey = `net(${sortedCodes.join(';')})`
              setItemEdits(prev => [...prev, {
                type: 'net' as const,
                key: netKey,
                label: netBuilderLabel.trim(),
                originalLabel: '',
                syntax: '',
                originalSyntax: '',
                isNew: true,
                isRemoved: false,
                noSort: false,
                originalNoSort: false,
              }])
              setNetBuilderOpen(false)
            }}
          >
            Add Net
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}

export default VariableEditorModal
